import arrowLeft from '@/static/assets/instant_everything/arrow_down_left.svg';
import arrowRight from '@/static/assets/instant_everything/arrow_down_right.svg';
import { Flex, font } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { I18nContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import Image from 'next/image';
import { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { INSTANT_EVERYTHING_DEFAULTS } from '../defaults';

interface Adjustment {
  readonly top?: {
    readonly sm?: string;
    readonly md?: string;
    readonly lg?: string;
  };
  readonly left?: {
    readonly sm?: string;
    readonly md?: string;
    readonly lg?: string;
  };
  readonly right?: {
    readonly sm?: string;
    readonly md?: string;
    readonly lg?: string;
  };
}

const AppSketchWrapper = styled(Flex)`
  position: relative;
  height: 365px;
  width: 100%;
  align-items: flex-end;
  justify-content: center;
  background: transparent;
  margin-top: 50px;

  ${MEDIA_QUERIES.tablet`
    margin-top: 100px
  `}

  ${MEDIA_QUERIES.tabletAndDesktop`
    align-items: flex-start;
  `}

  img {
    display: block;
    width: 100%;
    max-width: 315px;
    height: auto;

    ${MEDIA_QUERIES.tabletAndDesktop`
      max-width: 450px;
    `}
  }
`;

const ContentSection = styled(Flex)`
  position: absolute;
  flex-direction: column;
  text-align: center;
  gap: 10px;
  white-space: pre;

  img {
    display: none;
    height: 30px;
    width: auto;

    ${MEDIA_QUERIES.desktop`
      display: block
    `}
  }
`;

const ContentSectionLeft = styled(ContentSection)<Adjustment>`
  top: ${props => props.top?.sm ?? '0'};
  left: 50%;
  margin-left: ${props => props.left?.sm ?? '-145px'};
  align-items: flex-end;

  ${MEDIA_QUERIES.tablet`
    top: ${props => props.top?.md ?? '-65px'};
    left: 50%;
    margin-left: ${props => props.left?.md ?? '-170px'};
  `}

  ${MEDIA_QUERIES.desktop`
    top:${props => props.top?.lg ?? '60px'};
    left: 50%;
    margin-left: ${props => props.left?.lg ?? '-450px'};
  `}
`;

const ContentSectionRight = styled(ContentSection)<Adjustment>`
  top: ${props => props.top?.sm ?? '50px'};
  right: 50%;
  margin-right: ${props => props.right?.sm ?? '-162px'};
  align-items: flex-start;

  ${MEDIA_QUERIES.tablet`
    top: ${props => props.top?.md ?? '10px'};
    right: 50%;
    margin-right: ${props => props.right?.md ?? '-190px'};
  `}

  ${MEDIA_QUERIES.desktop`
    top: ${props => props.top?.lg ?? '30px'};
    right: 50%;
    margin-right: ${props => props.right?.lg ?? '-410px'};
  `}
`;

const ContentSectionContent = styled.p`
  text-wrap: balance;
  ${font('main', { fontSize: '2.0rem' })}
  padding: 0 20px;

  ${MEDIA_QUERIES.desktop`
    ${font('main', { fontSize: '2.8rem' })}
  `}
`;

export interface AppSketchProps {
  readonly appSketchImage?: string;
  readonly contentLeft: string;
  readonly contentRight: string;
  readonly adjustments?: {
    readonly contentSectionLeft?: Adjustment;
    readonly contentSectionRight?: Adjustment;
  };
}

const { appSketchImage: defaultAppSketchImage } = INSTANT_EVERYTHING_DEFAULTS;

export const AppSketch: React.FC<AppSketchProps> = ({ appSketchImage, contentLeft, contentRight, adjustments }) => {
  const { region, lang } = useContext(I18nContext);

  const finalAppSketchImage = useMemo(() => {
    return appSketchImage == null || appSketchImage === ''
      ? (defaultAppSketchImage[region][lang] as string)
      : appSketchImage;
  }, [appSketchImage, region, lang]);

  return (
    <AppSketchWrapper>
      <Image alt="" src={finalAppSketchImage} />
      <ContentSectionLeft {...adjustments?.contentSectionLeft}>
        <ContentSectionContent>{parse(contentLeft)}</ContentSectionContent>
        <Image alt="" src={arrowRight} />
      </ContentSectionLeft>
      <ContentSectionRight {...adjustments?.contentSectionRight}>
        <ContentSectionContent>{parse(contentRight)}</ContentSectionContent>
        <Image alt="" src={arrowLeft} />
      </ContentSectionRight>
    </AppSketchWrapper>
  );
};
